import { useCollection } from '@cloudscape-design/collection-hooks';
import { ContentLayout, SelectProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import Layout from '../../../../components/Layout';
import {
  FoodMenue,
  GetWeeklyFoodMenuesDocument,
  GetWeeklyFoodMenuesQuery,
  useDeleteWeeklyFoodMenueMutation,
  useGetAdditivesQuery,
  useGetAllergensQuery,
  useGetWeeklyFoodMenuesQuery,
} from '../../../../generated/graphql';
import useNotifications from '../../../../hooks/useNotifications';
import MetaTags from '../../../../lib/MetaTags';
import Create from './Create';
import Delete from './Delete';
import Edit from './Edit';
import { dayOptions } from './Form';
import { getMatchesCountText } from './table-config';

type EmptyStateProps = {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
};
function EmptyState({ title, subtitle, action }: EmptyStateProps) {
  return (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        {title}
      </Box>
      <Box variant="p" padding={{ bottom: 's' }} color="inherit">
        {subtitle}
      </Box>
      {action}
    </Box>
  );
}

const FoodMenues = () => {
  const params = useParams();
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const { data } = useGetWeeklyFoodMenuesQuery({
    variables: {
      Filter: {
        week: {
          eq: Number(params.week),
        },
      },
    },
  });
  const { data: additivesData } = useGetAdditivesQuery({
    variables: {
      Filter: {
        is_active: {
          eq: true,
        },
      },
    },
  });
  const { data: allergenesData } = useGetAllergensQuery({
    variables: {
      Filter: {
        is_active: {
          eq: true,
        },
      },
    },
  });

  const additiveOptions = useMemo(() => {
    if (additivesData?.listAdditives?.items) {
      return additivesData.listAdditives.items.map(
        (item) =>
          ({
            label: item?.text,
            value: item?.id,
            description: 'Zusatzstoff',
          }) as SelectProps.Option,
      );
    }
    return [];
  }, [additivesData]);

  const allergenOptions = useMemo(() => {
    if (allergenesData?.listAllergens?.items) {
      return allergenesData.listAllergens.items.map(
        (item) =>
          ({
            label: item?.text,
            value: item?.id,
            description: 'Allergen',
          }) as SelectProps.Option,
      );
    }
    return [];
  }, [allergenesData]);

  const [deleteWeeklyFoodMenue] = useDeleteWeeklyFoodMenueMutation();

  const {
    notifications,
    notifyDeletedOrUpdated,
    notifyFailed,
    notifyInProgress,
  } = useNotifications();

  const viewData = useMemo(() => {
    if (!data?.listWeeklyFoodMenues?.items) return [];
    return data.listWeeklyFoodMenues.items.map((item) => {
      const displayday = item?.displayday ?? 0;
      return {
        ...item,
        price:
          new Intl.NumberFormat('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(Number(item?.price)) ?? '',
        subtitle: item?.subtitle ?? '-',
        foodtype: item?.foodtype?.label ?? '-',
        displayday: dayOptions.filter(
          (option) => Number(option.value) === displayday,
        )[0].label,
        is_active: item?.is_active ? 'Ja' : 'Nein',
        updated_at: item?.updated_at
          ? DateTime.fromSeconds(item.updated_at).toLocaleString(
              DateTime.DATETIME_MED,
            )
          : '-',
      };
    });
  }, [data]);

  const [preferences] = useState({
    contentDisplay: [
      { id: 'id', visible: false },
      { id: 'title', visible: true },
      { id: 'subtitle', visible: true },
      { id: 'price', visible: true },
      { id: 'foodtype', visible: true },
      { id: 'displayday', visible: true },
      { id: 'is_active', visible: true },
      { id: 'updated_at', visible: true },
    ],
  });

  const {
    items,
    actions,
    filteredItemsCount,
    collectionProps,
    filterProps,
    actions: { setSelectedItems },
  } = useCollection(viewData, {
    filtering: {
      empty: (
        <EmptyState
          title="Keine Einträge gefunden"
          action={
            <Button onClick={() => setCreateModal(true)}>Erstellen</Button>
          }
        />
      ),
      noMatch: (
        <EmptyState
          title="Keine Einträge gefunden"
          action={
            <Button onClick={() => actions.setFiltering('')}>
              Filter zurücksetzen
            </Button>
          }
        />
      ),
    },
    sorting: {},
    selection: {},
  });
  const { selectedItems, onSelectionChange } = collectionProps;

  const onDeleteDiscard = () => setDeleteModal(false);
  const onDeleteConfirm = useCallback(async () => {
    setSelectedItems([]);
    if (selectedItems && selectedItems.length > 0 && selectedItems[0]) {
      const selectedItem = selectedItems[0];
      const messageId = uuidv4();
      notifyInProgress({ id: messageId, header: 'Eintrag wird gelöscht...' });
      setDeleteModal(false);
      deleteWeeklyFoodMenue({
        variables: { input: { id: selectedItem.id ?? '' } },
        update: (cache) => {
          const existingData = cache.readQuery({
            query: GetWeeklyFoodMenuesDocument,
            variables: {
              Filter: {
                week: {
                  eq: Number(params.week),
                },
              },
            },
          }) as GetWeeklyFoodMenuesQuery;
          const oldData = existingData.listWeeklyFoodMenues
            ?.items as FoodMenue[];
          const newData = oldData.filter((item) => item.id !== selectedItem.id);
          cache.writeQuery({
            query: GetWeeklyFoodMenuesDocument,
            variables: {
              Filter: {
                week: {
                  eq: Number(params.week),
                },
              },
            },
            data: {
              listWeeklyFoodMenues: { items: newData },
            },
          });
        },
      })
        .then(async (data) => {
          const result = data.data?.deleteFoodMenue;
          if (result) {
            notifyInProgress();
            notifyDeletedOrUpdated({
              id: messageId,
              type: 'success',
              content: `Eintrag mit dem Namen "${selectedItem.title}" wurde gelöscht.`,
              header: 'Gelöscht',
              dismissible: true,
            });
          }
        })
        .catch((error) => {
          notifyInProgress();
          notifyFailed({
            id: messageId,
            content: JSON.stringify(error),
            header: 'Ein Fehler ist aufgetreten',
            dismissible: true,
          });
        });
    }
  }, [
    deleteWeeklyFoodMenue,
    notifyFailed,
    notifyInProgress,
    selectedItems,
    setSelectedItems,
    notifyDeletedOrUpdated,
    params.week,
  ]);

  return (
    <Layout
      notifications={notifications}
      breadcrumbs={[
        { text: 'Webseite', href: '/website' },
        { text: 'Speisekarte', href: '/website/food' },
        {
          text: `Woche ${params.week}`,
          href: '/website/food/menues',
        },
      ]}
    >
      <MetaTags title={`Woche ${params.week} | Theo's Bistro`} />
      <ContentLayout
        header={<Header variant="h1">{`Woche ${params.week}`}</Header>}
      >
        <SpaceBetween size="m">
          <Table
            {...collectionProps}
            renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
              `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
            }
            onSelectionChange={onSelectionChange}
            selectedItems={selectedItems}
            stickyColumns={{ first: 1, last: 0 }}
            columnDefinitions={[
              {
                id: 'title',
                header: 'Titel',
                cell: (e) => e.title,
                sortingField: 'title',
                isRowHeader: true,
              },
              {
                id: 'subtitle',
                header: 'Untertitel',
                cell: (e) => e.subtitle,
                sortingField: 'subtitle',
                isRowHeader: true,
              },
              {
                id: 'price',
                header: 'Preis',
                cell: (e) => e.price,
                sortingField: 'price',
                isRowHeader: true,
              },
              {
                id: 'foodtype',
                header: 'Typ',
                cell: (e) => e.foodtype,
                sortingField: 'foodtype',
                isRowHeader: true,
              },
              {
                id: 'displayday',
                header: 'Tag',
                cell: (e) => e.displayday,
                sortingField: 'displayday',
                isRowHeader: true,
              },
              {
                id: 'is_active',
                header: 'Aktive',
                sortingField: 'is_active',
                cell: (e) => e.is_active,
              },
              {
                id: 'updated_at',
                header: 'Zuletzt bearbeitet',
                cell: (e) => e.updated_at,
                sortingField: 'updated_at',
                isRowHeader: true,
              },
            ]}
            columnDisplay={preferences.contentDisplay}
            enableKeyboardNavigation
            items={items}
            loadingText="Lade ..."
            selectionType="single"
            trackBy="id"
            filter={
              <TextFilter
                {...filterProps}
                countText={getMatchesCountText(filteredItemsCount)}
                filteringPlaceholder="Einträge durchsuchen"
              />
            }
            header={
              <Header
                counter={
                  selectedItems && selectedItems.length
                    ? `(${selectedItems.length}/${viewData.length})`
                    : `(${viewData.length})`
                }
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <ButtonDropdown
                      onItemClick={(event) => {
                        event.preventDefault();
                        setDeleteModal(true);
                      }}
                      disabled={isEmpty(selectedItems)}
                      items={[
                        {
                          id: 'delete',
                          text: 'Löschen',
                          external: false,
                        },
                      ]}
                      mainAction={{
                        disabled: isEmpty(selectedItems),
                        onClick: () => setEditModal(true),
                        text: 'Bearbeiten',
                      }}
                    />
                    <Button
                      variant="primary"
                      onClick={() => setCreateModal(true)}
                    >
                      Erstellen
                    </Button>
                  </SpaceBetween>
                }
              >
                Einträge
              </Header>
            }
          />
        </SpaceBetween>
        <Create
          setVisible={setCreateModal}
          visible={createModal}
          notifyInProgress={notifyInProgress}
          notifyDeletedOrUpdated={notifyDeletedOrUpdated}
          notifyFailed={notifyFailed}
          additiveOptions={additiveOptions}
          allergenOptions={allergenOptions}
        />
        {selectedItems && selectedItems.length > 0 ? (
          <>
            <Edit
              setVisible={setEditModal}
              visible={editModal}
              selectedId={selectedItems[0].id}
              notifyInProgress={notifyInProgress}
              notifyDeletedOrUpdated={notifyDeletedOrUpdated}
              notifyFailed={notifyFailed}
              additiveOptions={additiveOptions}
              allergenOptions={allergenOptions}
            />
            <Delete
              visible={deleteModal}
              onDiscard={onDeleteDiscard}
              onDelete={onDeleteConfirm}
              selectedItems={selectedItems}
            />
          </>
        ) : null}
      </ContentLayout>
    </Layout>
  );
};

export default FoodMenues;
